import { useEffect, useMemo, useState } from "react";
import { browserStorage, storageEntries } from "@/shared/browser-storage";
import { alpha, useTheme } from "@mui/material/styles";
import { useSearchParams } from "react-router";
import { useSelectedDeployment } from "@/features/deployment-selection/hooks/use-selected-deployment";
import { LiveViewView } from "@/features/deployment-dashboard/live-view/live-view-view";
import { useLiveTracksStream } from "@/features/deployment-dashboard/live-view/hooks/use-live-tracks-stream";
import { useSiteTotalPopulationChart } from "@/features/deployment-dashboard/live-view/hooks/use-site-total-population-chart";
import { useLiveSensors } from "@/features/deployment-dashboard/live-view/hooks/use-live-sensors";
import { useLiveQueuesCurrent } from "@/features/deployment-dashboard/live-view/hooks/use-live-queues-current";
import { useLiveQueueConfigs } from "@/features/deployment-dashboard/live-view/hooks/use-live-queue-configs";
import { useAreaAois } from "@/features/deployment-dashboard/live-view/hooks/use-area-aois";
import type { LiveViewMode } from "@/features/deployment-dashboard/live-view/components/live-view-tabs";
import type { LiveViewSidebarTab } from "@/features/deployment-dashboard/live-view/live-view-side-panel";
import type {
  Aoi,
  AoiDetailsInput,
  AoiEditorMode,
  AoiWorldPoint,
} from "@/features/deployment-dashboard/live-view/model/aoi";
import type { QueueRagStatus } from "@/features/deployment-dashboard/live-view/model/queue";
import {
  DEFAULT_LIVE_VIEW_TIME_RANGE,
  type LiveViewTimeRange,
} from "@/features/deployment-dashboard/live-view/model/time-range";
import { deriveLiveTrackMarkers } from "@/features/deployment-dashboard/live-view/utils/derive-live-track-markers";
import { deriveObservationAreaBoundaries } from "@/features/deployment-dashboard/live-view/utils/derive-observation-area-boundaries";
import { deriveSensorPoints } from "@/features/deployment-dashboard/live-view/utils/derive-sensor-points";
import { deriveLiveViewStats } from "@/features/deployment-dashboard/live-view/utils/derive-live-view-stats";
import { deriveLiveTrackLayers } from "@/features/deployment-dashboard/live-view/utils/derive-live-track-layers";
import { deriveAoiMapLayers } from "@/features/deployment-dashboard/live-view/utils/derive-aoi-map-layers";
import {
  deriveQueueMapBoundaries,
  deriveQueueMapLines,
} from "@/features/deployment-dashboard/live-view/utils/derive-queue-map-lines";
import {
  compileAoiPopulationIndex,
  deriveAoiDensities,
  deriveAoiPopulations,
} from "@/features/deployment-dashboard/live-view/utils/derive-aoi-populations";
import { deriveSecurityLaneStatuses } from "@/features/deployment-dashboard/live-view/utils/derive-security-lane-states";
import { isPointInsidePolygon } from "@/features/deployment-dashboard/live-view/utils/polygon-geometry";
import { useFacilitySchematicAssets } from "@/features/deployment-dashboard/widgets/panels/facility-schematic/hooks/use-facility-schematic-assets";
import { useAreaMaps } from "@/features/deployment-dashboard/shared/hooks/use-area-maps";
import { useAreas } from "@/features/deployment-dashboard/shared/hooks/use-areas";
import { useAreaCurrentPopulation } from "@/features/deployment-dashboard/shared/hooks/use-area-current-population";
import { useThroughputLineConfigs } from "@/features/deployment-dashboard/shared/hooks/use-throughput-line-configs";

const TRACK_TTL_TICKS = 12;

export function LiveViewContainer() {
  const theme = useTheme();
  const { deploymentId } = useSelectedDeployment();
  const [searchParams] = useSearchParams();
  const requestedAreaId = searchParams.get("area");
  const { data: areas = [], isSuccess: areasLoaded } = useAreas(deploymentId);
  const { data: sensors = [] } = useLiveSensors(deploymentId);
  const liveQueues = useLiveQueuesCurrent(deploymentId);
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [showSensors, setShowSensors] = useState(true);
  const [showDetections, setShowDetections] = useState(true);
  const [showTracks, setShowTracks] = useState(false);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(true);
  const [viewMode, setViewMode] = useState<LiveViewMode>("map");
  const [timeRange, setTimeRange] = useState<LiveViewTimeRange>(DEFAULT_LIVE_VIEW_TIME_RANGE);
  const [focusAreasRequestId, setFocusAreasRequestId] = useState(0);
  const [isAreasFocused, setIsAreasFocused] = useState(true);
  const [activeSidebarTab, setActiveSidebarTab] = useState<LiveViewSidebarTab>("areasOfInterest");
  const [selectedQueueId, setSelectedQueueId] = useState<string | null>(null);
  const [selectedAoiId, setSelectedAoiId] = useState<string | null>(null);
  const [aoiEditorMode, setAoiEditorMode] = useState<AoiEditorMode>(null);
  const [editingAoiId, setEditingAoiId] = useState<string | null>(null);
  const [aoiDraftPoints, setAoiDraftPoints] = useState<AoiWorldPoint[]>([]);
  const [hoveredAoiPointIndex, setHoveredAoiPointIndex] = useState<number | null>(null);
  // Set once an existing AOI's vertex is removed mid-edit, so the draft's
  // closing edge stops rendering as a real, solid edge and instead shows the
  // same pending/pulsing preview as a brand-new shape until saved again.
  const [aoiClosurePending, setAoiClosurePending] = useState(false);
  // Null until the initial area selection (URL, saved choice, default, or first area)
  // has been determined; area-scoped queries stay disabled until then so they never
  // briefly subscribe to the wrong scope.
  const [areaSelection, setAreaSelection] = useState<{
    deploymentId: string;
    areaId: string | null;
  }>({ deploymentId, areaId: null });
  const selectedAreaId = areaSelection.deploymentId === deploymentId ? areaSelection.areaId : null;

  useEffect(() => {
    if (!areasLoaded || selectedAreaId !== null) {
      return;
    }

    const requestedArea = areas.find((area) => area.id === requestedAreaId);
    if (requestedArea != null) {
      setAreaSelection({ deploymentId, areaId: requestedArea.id });
      return;
    }

    const savedAreaId = browserStorage.read(storageEntries.preferredArea(deploymentId));
    const savedArea = areas.find((area) => area.id === savedAreaId);
    const defaultArea = areas.find((area) => area.default === true);
    const areaId = savedArea?.id ?? defaultArea?.id ?? areas[0]?.id ?? null;
    if (areaSelection.deploymentId !== deploymentId || areaSelection.areaId !== areaId) {
      setAreaSelection({ deploymentId, areaId });
    }
  }, [areasLoaded, areas, selectedAreaId, requestedAreaId, deploymentId, areaSelection]);

  const selectedAreaIds = useMemo(
    () => (selectedAreaId !== null ? [selectedAreaId] : []),
    [selectedAreaId]
  );

  const {
    latestAreaFrame,
    trackHistory,
    tick,
    status: liveTracksStatus,
  } = useLiveTracksStream(deploymentId, selectedAreaId, selectedAreaId !== null, TRACK_TTL_TICKS);
  const latestAreaFrames = useMemo(
    () => (latestAreaFrame ? { [latestAreaFrame.area_id]: latestAreaFrame } : {}),
    [latestAreaFrame]
  );
  const selectedAreaMaps = useAreaMaps(deploymentId, selectedAreaIds);
  const loadedAreaMaps = useMemo(
    () =>
      selectedAreaMaps.flatMap((areaMap) =>
        areaMap.data && areaMap.data.areaId === selectedAreaId ? [areaMap.data] : []
      ),
    [selectedAreaMaps, selectedAreaId]
  );
  const rawObservationAreas = useMemo(
    () => loadedAreaMaps.flatMap((areaMap) => areaMap.observationAreas),
    [loadedAreaMaps]
  );
  const liveViewStats = useMemo(
    () => deriveLiveViewStats(latestAreaFrames, loadedAreaMaps),
    [latestAreaFrames, loadedAreaMaps]
  );
  const selectedAreaScopeKey = selectedAreaId ?? "none";
  const populationChart = useSiteTotalPopulationChart(
    deploymentId,
    selectedAreaIds,
    timeRange,
    selectedAreaId !== null && areasLoaded
  );
  const selectedArea = useMemo(
    () => areas.find((area) => area.id === selectedAreaId) ?? null,
    [areas, selectedAreaId]
  );
  const summaryTitle = selectedArea?.name ?? "No Area Selected";
  const liveQueueConfigs = useLiveQueueConfigs(deploymentId, selectedAreaId);
  const areaQueues = useMemo(
    () => liveQueues.queues.filter((queue) => queue.area_id === selectedAreaId),
    [liveQueues.queues, selectedAreaId]
  );
  const { lines: throughputLines } = useThroughputLineConfigs(
    deploymentId,
    selectedArea === null ? [] : [selectedArea],
    "queue_boundary"
  );

  const areaCurrentPopulation = useAreaCurrentPopulation(
    deploymentId,
    selectedAreaId ?? undefined,
    selectedAreaId !== null
  );

  const displayedAreaId = selectedAreaId ?? undefined;
  const {
    aois: areaAois,
    canCreate: canCreateAoi,
    canUpdate: canUpdateAoi,
    canDelete: canDeleteAoi,
    createAoi,
    updateAoi,
    deleteAoi,
    isSaving: isSavingAoi,
    error: aoiQueryError,
    mutationError: aoiMutationError,
    resetMutationError,
  } = useAreaAois(deploymentId, selectedAreaIds);
  // Defensive re-scoping: `useAreaAois` already queries only `selectedAreaIds`,
  // but this guards against a stale/mismatched response ever being presented
  // as the selected area's AOIs.
  const aois = useMemo(
    () => (selectedAreaId ? areaAois.filter((aoi) => aoi.areaId === selectedAreaId) : []),
    [areaAois, selectedAreaId]
  );
  const { transform, floorplanImageSize, sensorIds } = useFacilitySchematicAssets(
    deploymentId,
    displayedAreaId
  );
  const liveTracks = useMemo(
    () => deriveLiveTrackMarkers(transform, latestAreaFrames),
    [transform, latestAreaFrames]
  );
  const trails = useMemo(
    () => (showTracks ? deriveLiveTrackLayers(transform, trackHistory, tick) : []),
    [transform, trackHistory, tick, showTracks]
  );

  const sensorPoints = useMemo(() => {
    // Render no markers until the selected area's map (and its configured
    // sensorIds) is available, and never plot another area's sensors even
    // when they share the same coordinate frame.
    if (!selectedAreaId || sensorIds.length === 0) return [];
    const allowedSensorIds = new Set(sensorIds);
    const areaSensors = sensors.filter((sensor) => allowedSensorIds.has(sensor.sensor_id));
    return deriveSensorPoints(transform, areaSensors);
  }, [selectedAreaId, sensorIds, sensors, transform]);
  const observationAreas = useMemo(
    () => deriveObservationAreaBoundaries(transform, rawObservationAreas),
    [transform, rawObservationAreas]
  );
  const queueStatuses = useMemo(
    () => new Map(areaQueues.map((queue) => [queue.queue_id, queue.status] as const)),
    [areaQueues]
  );
  const queueMetrics = useMemo(
    () =>
      new Map(
        areaQueues.map((queue) => [
          queue.queue_id,
          {
            peopleInQueue: queue.peopleInQueue,
            estimatedWaitMinutes: queue.estimatedWaitMinutes,
            waitDataStatus: queue.waitDataStatus,
            waitQualityReasons: queue.waitQualityReasons,
          },
        ])
      ),
    [areaQueues]
  );
  const queueBoundaries = useMemo(
    () =>
      deriveQueueMapBoundaries(
        transform,
        liveQueueConfigs.queueConfigs,
        selectedQueueId,
        queueStatuses,
        queueMetrics,
        {
          byStatus: new Map<QueueRagStatus, string>([
            ["green", theme.palette.success.main],
            ["amber", theme.palette.warning.main],
            ["red", theme.palette.error.main],
          ]),
          unavailable: theme.palette.text.disabled,
          labelText: theme.palette.text.primary,
          labelBackground: alpha(theme.palette.background.paper, 0.92),
        }
      ),
    [liveQueueConfigs.queueConfigs, queueMetrics, queueStatuses, selectedQueueId, theme, transform]
  );
  const queueDirectionalLines = useMemo(
    () =>
      deriveQueueMapLines(
        transform,
        liveQueueConfigs.queueConfigs,
        throughputLines,
        selectedQueueId,
        {
          // Neutral (not semantic-colored) so direction is read from the
          // IN/OUT label and arrow, not hue; a dark halo (added in
          // option.ts) keeps it visible over the heatmap.
          gate: theme.palette.common.white,
        }
      ),
    [liveQueueConfigs.queueConfigs, selectedQueueId, theme, throughputLines, transform]
  );
  const aoiPopulationIndex = useMemo(() => compileAoiPopulationIndex(aois), [aois]);
  // The selected area's live frame receives a new identity for every accepted update,
  // while static AOI geometry remains compiled across ticks.
  const aoiPopulations = useMemo(
    () => deriveAoiPopulations(aoiPopulationIndex, latestAreaFrames),
    [aoiPopulationIndex, latestAreaFrames]
  );
  const securityLaneStatuses = useMemo(
    () => deriveSecurityLaneStatuses(aois, aoiPopulations, undefined, latestAreaFrame !== null),
    [aois, aoiPopulations, latestAreaFrame]
  );
  const aoiMapLayers = useMemo(
    () =>
      deriveAoiMapLayers(transform, aois, selectedAoiId, aoiDraftPoints, {
        editorMode: aoiEditorMode,
        hoveredPointIndex: hoveredAoiPointIndex,
        closurePending: aoiClosurePending,
        securityLaneStatuses,
      }),
    [
      transform,
      aois,
      selectedAoiId,
      aoiDraftPoints,
      aoiEditorMode,
      hoveredAoiPointIndex,
      aoiClosurePending,
      securityLaneStatuses,
    ]
  );
  const aoiDensities = useMemo(
    () => deriveAoiDensities(aois, aoiPopulations),
    [aois, aoiPopulations]
  );
  const selectedAoi = useMemo(
    () => aois.find((aoi) => aoi.id === selectedAoiId) ?? null,
    [aois, selectedAoiId]
  );
  const populationLabel = selectedAoi ? selectedAoi.name : "Total";
  const populationCount = selectedAoi
    ? (aoiPopulations[selectedAoi.id] ?? 0)
    : liveViewStats.population;

  const aoiError = aoiMutationError ?? aoiQueryError;

  useEffect(() => {
    setSelectedQueueId(null);
    setSelectedAoiId(null);
    setAoiEditorMode(null);
    setEditingAoiId(null);
    setAoiDraftPoints([]);
    setHoveredAoiPointIndex(null);
    setAoiClosurePending(false);
  }, [displayedAreaId, selectedAreaScopeKey]);

  const startCreateAoi = () => {
    resetMutationError();
    setIsSummaryExpanded(true);
    setSelectedAoiId(null);
    setEditingAoiId(null);
    setAoiDraftPoints([]);
    setAoiClosurePending(false);
    setAoiEditorMode("create");
  };

  const startEditAoi = (aoi: Aoi) => {
    resetMutationError();
    setIsSummaryExpanded(true);
    setSelectedAoiId(aoi.id);
    setEditingAoiId(aoi.id);
    setAoiDraftPoints(aoi.points.map((point) => ({ ...point })));
    setAoiClosurePending(false);
    setAoiEditorMode("edit");
  };

  const cancelAoiEdit = () => {
    resetMutationError();
    setAoiEditorMode(null);
    setEditingAoiId(null);
    setAoiDraftPoints([]);
    setHoveredAoiPointIndex(null);
    setAoiClosurePending(false);
  };

  const deleteAoiById = async (aoiId: string) => {
    try {
      await deleteAoi(aoiId);
      if (selectedAoiId === aoiId) setSelectedAoiId(null);
      if (editingAoiId === aoiId) cancelAoiEdit();
    } catch {
      // TanStack Query exposes the mutation error to the side panel.
    }
  };

  const toggleAoi = (aoiId: string) => {
    setSelectedAoiId((current) => (current === aoiId ? null : aoiId));
  };

  const toggleQueue = (queueId: string) => {
    setSelectedQueueId((current) => (current === queueId ? null : queueId));
  };

  const selectAoiAtPoint = (point: AoiWorldPoint) => {
    const match = aois.find((aoi) => isPointInsidePolygon(point, aoi.points));
    if (match) {
      toggleAoi(match.id);
    } else {
      setSelectedAoiId(null);
    }
  };

  const handleHoverAoiPoint = (pointId: string | null) => {
    if (pointId == null) {
      setHoveredAoiPointIndex(null);
      return;
    }

    const match = pointId.match(/-(\d+)$/);
    setHoveredAoiPointIndex(match ? Number(match[1]) : null);
  };

  const deleteAoiDraftPoint = (pointIndex: number) => {
    setAoiDraftPoints((points) => points.filter((_, index) => index !== pointIndex));
    // Removing any vertex from an existing AOI's draft means the shape is no
    // longer guaranteed to be closed the way it was persisted, so the
    // closing edge falls back to the same pending preview as a new shape
    // until the user saves again.
    setAoiClosurePending(true);
  };

  const insertAoiDraftPoint = (insertionPointId: string) => {
    const match = insertionPointId.match(/-(\d+)$/);
    if (!match) return;
    const edgeIndex = Number(match[1]);

    setAoiDraftPoints((points) => {
      if (edgeIndex < 0 || edgeIndex >= points.length) return points;
      const start = points.at(edgeIndex)!;
      const end = points.at((edgeIndex + 1) % points.length)!;
      const midpoint = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
      const next = [...points];
      next.splice(edgeIndex + 1, 0, midpoint);
      return next;
    });
  };

  const saveAoi = async (input: AoiDetailsInput) => {
    if (!input.areaId || aoiDraftPoints.length < 3) return;

    try {
      if (aoiEditorMode === "create") {
        await createAoi({ ...input, points: aoiDraftPoints });
      } else if (aoiEditorMode === "edit" && editingAoiId) {
        await updateAoi({
          ...input,
          aoiId: editingAoiId,
          points: aoiDraftPoints,
        });
      } else {
        return;
      }

      cancelAoiEdit();
    } catch {
      // TanStack Query exposes the mutation error to the side panel.
    }
  };

  return (
    <LiveViewView
      deploymentId={deploymentId}
      areaId={displayedAreaId}
      liveTracks={liveTracks}
      heatmapTransform={transform}
      heatmapMapSize={floorplanImageSize}
      heatmapTracks={liveTracks}
      areas={areas}
      selectedAreaId={selectedAreaId}
      onSelectedAreaIdChange={(areaId) => {
        setAreaSelection({ deploymentId, areaId });
        if (areaId !== null) {
          browserStorage.write(storageEntries.preferredArea(deploymentId), areaId);
        }
        setIsAreasFocused(true);
        setFocusAreasRequestId((id) => id + 1);
      }}
      showHeatmap={showHeatmap}
      onShowHeatmapChange={setShowHeatmap}
      sensorPoints={sensorPoints}
      showSensors={showSensors}
      onShowSensorsChange={setShowSensors}
      showDetections={showDetections}
      onShowDetectionsChange={setShowDetections}
      trails={trails}
      showTracks={showTracks}
      onShowTracksChange={setShowTracks}
      observationAreas={observationAreas}
      aois={aois}
      selectedAoiId={selectedAoiId}
      onToggleAoi={toggleAoi}
      canModifyAois={canCreateAoi || canUpdateAoi}
      aoiEditorMode={aoiEditorMode}
      editingAoiId={editingAoiId}
      aoiDraftPoints={aoiDraftPoints}
      aoiBoundaries={aoiMapLayers.boundaries}
      aoiDraftPointMarkers={aoiMapLayers.draftPoints}
      isSavingAoi={isSavingAoi}
      aoiError={
        aoiError instanceof Error
          ? aoiError.message
          : aoiError
            ? "Unable to load or save areas of interest."
            : null
      }
      onStartCreateAoi={startCreateAoi}
      onStartEditAoi={startEditAoi}
      onCancelAoiEdit={cancelAoiEdit}
      onUndoAoiPoint={() => setAoiDraftPoints((points) => points.slice(0, -1))}
      onClearAoiPoints={() => setAoiDraftPoints([])}
      onDeleteAoiPoint={deleteAoiDraftPoint}
      onMoveAoiPoint={(pointId, point) => {
        const pointIndex = aoiMapLayers.draftPoints.findIndex((marker) => marker.id === pointId);
        if (pointIndex < 0) return;
        setAoiDraftPoints((points) =>
          points.map((existingPoint, index) => (index === pointIndex ? point : existingPoint))
        );
      }}
      onMapAoiPoint={(point) =>
        setAoiDraftPoints((points) => (aoiEditorMode === "create" ? [...points, point] : points))
      }
      onInsertAoiPoint={insertAoiDraftPoint}
      aoiInsertionPointMarkers={aoiMapLayers.insertionPoints}
      onHoverAoiPoint={handleHoverAoiPoint}
      hoveredAoiPointIndex={hoveredAoiPointIndex}
      onSelectAoiAtPoint={selectAoiAtPoint}
      onDeleteAoi={(aoiId) => void deleteAoiById(aoiId)}
      canDeleteAoi={canDeleteAoi}
      onSaveAoi={(input) => void saveAoi(input)}
      aoiPopulations={aoiPopulations}
      aoiDensities={aoiDensities}
      securityLaneStatuses={securityLaneStatuses}
      populationLabel={populationLabel}
      populationCount={populationCount}
      liveViewStats={liveViewStats}
      areaCurrentPopulation={areaCurrentPopulation}
      liveTracksStatus={liveTracksStatus}
      liveTracksHasFrame={latestAreaFrame !== null}
      queues={areaQueues}
      selectedQueueId={selectedQueueId}
      onToggleQueue={toggleQueue}
      queueBoundaries={queueBoundaries}
      queueDirectionalLines={queueDirectionalLines}
      activeSidebarTab={activeSidebarTab}
      onActiveSidebarTabChange={setActiveSidebarTab}
      queuesLoading={liveQueues.isLoading}
      queuesError={liveQueues.error}
      summaryTitle={summaryTitle}
      isSummaryExpanded={isSummaryExpanded}
      onSummaryExpandedChange={setIsSummaryExpanded}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      timeRange={timeRange}
      onTimeRangeChange={setTimeRange}
      populationChart={populationChart.data}
      populationChartIsLoading={populationChart.isLoading}
      populationChartError={populationChart.error}
      populationChartHasGrant={populationChart.hasGrant}
      focusAreasRequestId={focusAreasRequestId}
      onResetFocus={() => setFocusAreasRequestId((id) => id + 1)}
      isAreasFocused={isAreasFocused}
      onAreasFocusedChange={setIsAreasFocused}
    />
  );
}
